/**
 * Collections / Defaulters dashboard.
 *
 * KPI strip (30-day window):
 *   total outstanding | defaulters count | avg days overdue | recovered (30d)
 *   | reminders sent (30d).
 *
 * Aging chart: 5-bucket bar chart (current / 0-30 / 31-60 / 61-90 / 90+) of
 * outstanding INR. Pure SVG — keeps the bundle slim (Recharts not imported).
 *
 * Defaulter list: bucket filter + search + as_of date. Per-row "Send
 * reminder", "Copy pay-link" and a link to the unit detail page. Multi-
 * select enables bulk reminder send.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bell,
  ChevronRight,
  ClipboardCopy,
  HandCoins,
  Search,
  Wallet,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { TabsPills } from '@/components/ui/tabs-pills';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate } from '@/lib/format';

type Bucket = 'current' | '0_30' | '31_60' | '61_90' | '90_plus';
type Channel = 'email' | 'sms' | 'whatsapp' | 'app_notification';

interface DefaulterRow {
  unit_id: string;
  unit_label: string;
  resident_name: string | null;
  resident_phone: string | null;
  total_outstanding_inr: number;
  oldest_unpaid_due_date: string;
  days_overdue: number;
  bills_count_unpaid: number;
  bucket: Bucket;
}

interface DefaulterListResponse {
  items: DefaulterRow[];
  next_cursor: string | null;
  summary: {
    grand_total_inr: number;
    defaulters_count: number;
    totals_by_bucket: Record<Bucket, number>;
  };
}

interface CollectionsStats {
  window_days: number;
  total_outstanding_inr: number;
  defaulters_count: number;
  avg_days_overdue: number;
  recovered_in_window_inr: number;
  reminders_sent_in_window: number;
  notes_added_in_window: number;
}

const BUCKET_LABEL: Record<Bucket, string> = {
  current: 'Current',
  '0_30': '0–30 days',
  '31_60': '31–60 days',
  '61_90': '61–90 days',
  '90_plus': '90+ days'
};

const BUCKET_VARIANT: Record<Bucket, 'muted' | 'info' | 'warn' | 'alert'> = {
  current: 'muted',
  '0_30': 'info',
  '31_60': 'warn',
  '61_90': 'alert',
  '90_plus': 'alert'
};

const BUCKET_BAR_COLOR: Record<Bucket, string> = {
  current: 'fill-vt-text-muted',
  '0_30': 'fill-vt-info',
  '31_60': 'fill-vt-warn',
  '61_90': 'fill-vt-alert',
  '90_plus': 'fill-vt-alert'
};

const PAGE_SIZE = 50;

function inr(n: number): string {
  return `₹${(n ?? 0).toLocaleString('en-IN')}`;
}

function compactInr(n: number): string {
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}k`;
  return `₹${n}`;
}

export function CollectionsPage(): JSX.Element {
  const qc = useQueryClient();
  const [bucket, setBucket] = useState<Bucket | 'all'>('all');
  const [search, setSearch] = useState('');
  const [asOf, setAsOf] = useState<string>('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reminderTarget, setReminderTarget] = useState<DefaulterRow[] | null>(null);
  const [channel, setChannel] = useState<Channel>('email');
  const [template, setTemplate] = useState<'gentle' | 'firm' | 'final' | 'custom'>('gentle');
  const [customMessage, setCustomMessage] = useState('');

  const filtersHash = useMemo(
    () => JSON.stringify({ bucket, search: search.trim(), asOf }),
    [bucket, search, asOf]
  );
  useEffect(() => {
    setCursor(null);
    setSelected(new Set());
  }, [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['collections', 'defaulters', filtersHash, cursor],
    queryFn: () =>
      get<DefaulterListResponse>('/collections/defaulters', {
        params: {
          limit: PAGE_SIZE,
          ...(bucket !== 'all' ? { bucket } : {}),
          ...(search.trim() ? { q: search.trim() } : {}),
          ...(asOf ? { as_of: new Date(`${asOf}T23:59:59`).toISOString() } : {}),
          ...(cursor ? { cursor } : {})
        }
      }),
    placeholderData: (prev) => prev
  });

  const statsQuery = useQuery({
    queryKey: ['collections', 'stats', 30],
    queryFn: () => get<CollectionsStats>('/collections/stats?days=30'),
    refetchInterval: 60_000
  });

  // useCursorAccumulator wants {id}; map unit_id → id for the helper.
  const paginatorData = listQuery.data
    ? {
        items: listQuery.data.items.map((r) => ({ ...r, id: r.unit_id })),
        next_cursor: listQuery.data.next_cursor
      }
    : undefined;
  const paginator = useCursorAccumulator(paginatorData, cursor, filtersHash);
  const items = paginator.items;
  const summary = listQuery.data?.summary ?? {
    grand_total_inr: 0,
    defaulters_count: 0,
    totals_by_bucket: { current: 0, '0_30': 0, '31_60': 0, '61_90': 0, '90_plus': 0 }
  };

  const reminderMutation = useMutation({
    mutationFn: (payload: {
      unit_ids: string[];
      channel: Channel;
      template: 'gentle' | 'firm' | 'final' | 'custom';
      custom_message?: string;
    }) => post<{ count: number }>('/collections/reminders', payload),
    onSuccess: (res) => {
      toast.success(`Reminder sent to ${res.count} unit${res.count === 1 ? '' : 's'}`);
      setReminderTarget(null);
      setCustomMessage('');
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['collections'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  function copyPayLink(unitLabel: string, _unitId: string): void {
    // Placeholder URL — backend doesn't yet expose a public pay-by-link route.
    // When the unit-level pay page lands, swap to /pay/<unit_id>.
    const url = `https://app.hooddesk.com/pay/unit/${encodeURIComponent(unitLabel)}`;
    void navigator.clipboard.writeText(url).then(
      () => toast.success('Quick-pay link copied'),
      () => toast.error('Could not copy to clipboard')
    );
  }

  function toggleRow(unitId: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  }
  function selectAllVisible(): void {
    setSelected(new Set(items.map((r) => r.unit_id)));
  }

  const selectedRows = items.filter((r) => selected.has(r.unit_id));

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Collections"
        description="Track unpaid maintenance bills, send reminders, and follow up with defaulters. Reminders are logged for audit."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-vt-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiTile
          label="Total outstanding"
          value={statsQuery.data ? inr(statsQuery.data.total_outstanding_inr) : undefined}
          tone="alert"
          icon={<Wallet className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="Defaulters"
          value={statsQuery.data?.defaulters_count}
          tone="warn"
          icon={<Users className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="Avg days overdue"
          value={statsQuery.data?.avg_days_overdue}
          tone="info"
          icon={<AlertTriangle className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="Recovered (30d)"
          value={statsQuery.data ? inr(statsQuery.data.recovered_in_window_inr) : undefined}
          tone="ok"
          icon={<HandCoins className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
        <KpiTile
          label="Reminders (30d)"
          value={statsQuery.data?.reminders_sent_in_window}
          tone="muted"
          icon={<Bell className="h-4 w-4" />}
          loading={statsQuery.isLoading}
        />
      </div>

      {/* Aging chart */}
      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-3 flex items-center justify-between gap-vt-2">
            <div>
              <h2 className="text-vt-14 font-semibold text-vt-text">Aging breakdown</h2>
              <p className="text-vt-12 text-vt-text-muted">
                Outstanding amount split by overdue bucket. Click a bar to filter the table below.
              </p>
            </div>
            <Badge variant="muted" className="font-mono">
              {compactInr(summary.grand_total_inr)} total
            </Badge>
          </div>
          <AgingChart
            totals={summary.totals_by_bucket}
            activeBucket={bucket === 'all' ? null : bucket}
            onBucketClick={(b) => setBucket(bucket === b ? 'all' : b)}
          />
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-vt-3 flex flex-wrap items-end gap-vt-2">
          <TabsPills
            aria-label="Bucket filter"
            value={bucket}
            onChange={(v) => setBucket(v as Bucket | 'all')}
            options={[
              { value: 'all', label: 'All', count: summary.defaulters_count },
              { value: 'current', label: BUCKET_LABEL.current },
              { value: '0_30', label: BUCKET_LABEL['0_30'] },
              { value: '31_60', label: BUCKET_LABEL['31_60'] },
              { value: '61_90', label: BUCKET_LABEL['61_90'] },
              { value: '90_plus', label: BUCKET_LABEL['90_plus'] }
            ]}
          />
          <div className="ml-auto flex flex-wrap items-end gap-vt-2">
            <div className="flex min-w-[220px] flex-col gap-vt-1">
              <Label className="text-vt-12 text-vt-text-muted">Search</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-vt-text-muted" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Unit number, resident, phone"
                  className="pl-8"
                />
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label className="text-vt-12 text-vt-text-muted">As of</Label>
              <Input
                type="date"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedRows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-vt-2 rounded-vt-md border border-vt-primary/40 bg-vt-primary-100 px-vt-4 py-vt-2 text-vt-13 text-vt-primary">
          <span>
            {selectedRows.length} unit{selectedRows.length === 1 ? '' : 's'} selected — total
            outstanding {inr(selectedRows.reduce((s, r) => s + r.total_outstanding_inr, 0))}
          </span>
          <div className="flex items-center gap-vt-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setReminderTarget(selectedRows);
                setTemplate('gentle');
                setCustomMessage('');
                setChannel('email');
              }}
            >
              <Bell className="h-3.5 w-3.5" /> Send reminder to selected
            </Button>
          </div>
        </div>
      ) : null}

      {/* Defaulter table */}
      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="space-y-2 p-vt-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<HandCoins className="h-7 w-7" strokeWidth={1.5} />}
              title="No defaulters in this view"
              description="When residents fall behind on maintenance dues they'll appear here. Adjust filters or pick a different bucket."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all visible"
                      checked={selectedRows.length === items.length && items.length > 0}
                      onChange={(e) => (e.target.checked ? selectAllVisible() : setSelected(new Set()))}
                      className="h-4 w-4"
                    />
                  </TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Resident</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Oldest unpaid</TableHead>
                  <TableHead className="text-right">Days overdue</TableHead>
                  <TableHead>Bucket</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.unit_id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.unit_label}`}
                        checked={selected.has(r.unit_id)}
                        onChange={() => toggleRow(r.unit_id)}
                        className="h-4 w-4"
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to="/collections/$unitId"
                        params={{ unitId: r.unit_id }}
                        className="inline-flex items-center gap-vt-1 text-vt-primary hover:underline"
                      >
                        {r.unit_label}
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </Link>
                    </TableCell>
                    <TableCell className="text-vt-13">{r.resident_name ?? '—'}</TableCell>
                    <TableCell className="font-mono text-vt-12 text-vt-text-muted">
                      {r.resident_phone ?? '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-vt-13">
                      {inr(r.total_outstanding_inr)}
                    </TableCell>
                    <TableCell className="text-vt-12 text-vt-text-muted">
                      {formatDate(r.oldest_unpaid_due_date)}
                    </TableCell>
                    <TableCell className="text-right font-mono">{r.days_overdue}</TableCell>
                    <TableCell>
                      <Badge variant={BUCKET_VARIANT[r.bucket]}>{BUCKET_LABEL[r.bucket]}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-vt-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setReminderTarget([r]);
                            setTemplate('gentle');
                            setCustomMessage('');
                            setChannel('email');
                          }}
                        >
                          <Bell className="h-3 w-3" /> Remind
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyPayLink(r.unit_label, r.unit_id)}
                          title="Copy quick-pay link"
                          aria-label="Copy quick-pay link"
                        >
                          <ClipboardCopy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="p-vt-3">
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={listQuery.isFetching}
              onLoadMore={() => paginator.nextCursor && setCursor(paginator.nextCursor)}
              itemsShown={items.length}
              pageSize={PAGE_SIZE}
            />
          </div>
        </CardContent>
      </Card>

      {/* Reminder dialog */}
      <Dialog
        open={Boolean(reminderTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setReminderTarget(null);
            setCustomMessage('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send reminder</DialogTitle>
            <DialogDescription>
              {reminderTarget && reminderTarget.length === 1
                ? `Send a payment reminder to ${reminderTarget[0]!.unit_label} (${reminderTarget[0]!.resident_name ?? 'no resident on file'}).`
                : `Send a payment reminder to ${reminderTarget?.length ?? 0} units in the current selection.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-3 py-vt-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="reminder-channel">Channel</Label>
              <Select
                id="reminder-channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value as Channel)}
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="app_notification">App notification</option>
              </Select>
              <p className="text-vt-11 text-vt-text-muted">
                Delivery is logged for audit. Actual provider hand-off is configured separately.
              </p>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="reminder-template">Template</Label>
              <Select
                id="reminder-template"
                value={template}
                onChange={(e) =>
                  setTemplate(e.target.value as 'gentle' | 'firm' | 'final' | 'custom')
                }
              >
                <option value="gentle">Gentle</option>
                <option value="firm">Firm</option>
                <option value="final">Final notice</option>
                <option value="custom">Custom message</option>
              </Select>
            </div>
            {template === 'custom' ? (
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="reminder-custom">Custom message</Label>
                <Textarea
                  id="reminder-custom"
                  rows={4}
                  maxLength={500}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Type the message to send (max 500 chars)…"
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setReminderTarget(null)}
              disabled={reminderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!reminderTarget) return;
                if (template === 'custom' && !customMessage.trim()) {
                  toast.error('Custom message is required for the custom template');
                  return;
                }
                reminderMutation.mutate({
                  unit_ids: reminderTarget.map((r) => r.unit_id),
                  channel,
                  template,
                  ...(template === 'custom' ? { custom_message: customMessage.trim() } : {})
                });
              }}
              loading={reminderMutation.isPending}
              loadingText="Sending…"
            >
              <Bell className="h-4 w-4" /> Send reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
  icon,
  loading
}: {
  label: string;
  value: number | string | undefined;
  tone: 'alert' | 'warn' | 'ok' | 'info' | 'muted';
  icon: React.ReactNode;
  loading?: boolean;
}): JSX.Element {
  const TONE_CLS: Record<typeof tone, string> = {
    alert: 'bg-vt-alert-100 text-vt-alert',
    warn: 'bg-vt-warn-100 text-vt-warn',
    ok: 'bg-vt-safe-100 text-vt-safe',
    info: 'bg-vt-info-100 text-vt-info',
    muted: 'bg-vt-surface-2 text-vt-text-muted'
  };
  return (
    <Card>
      <CardContent className="p-vt-4">
        <div className="mb-vt-2 inline-flex items-center gap-vt-2 rounded-vt-pill px-2 py-1 text-vt-12 font-medium ring-1 ring-inset ring-vt-border">
          <span
            className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TONE_CLS[tone]}`}
          >
            {icon}
          </span>
          {label}
        </div>
        <div className="text-vt-24 font-semibold text-vt-text">
          {loading ? <Skeleton className="h-7 w-16" /> : (value ?? 0)}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AgingChart — inline SVG bar chart, 5 buckets. Pure presentational.
 * Click handler lets the table filter sync with the chart.
 */
function AgingChart({
  totals,
  activeBucket,
  onBucketClick
}: {
  totals: Record<Bucket, number>;
  activeBucket: Bucket | null;
  onBucketClick: (b: Bucket) => void;
}): JSX.Element {
  const buckets: Bucket[] = ['current', '0_30', '31_60', '61_90', '90_plus'];
  const max = Math.max(1, ...buckets.map((b) => totals[b] ?? 0));
  const W = 800;
  const H = 200;
  const PAD_LEFT = 20;
  const PAD_RIGHT = 20;
  const PAD_TOP = 12;
  const PAD_BOTTOM = 36;
  const innerW = W - PAD_LEFT - PAD_RIGHT;
  const innerH = H - PAD_TOP - PAD_BOTTOM;
  const colW = innerW / buckets.length;
  const barW = colW * 0.6;

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-48 w-full" role="img" aria-label="Aging breakdown">
        {/* Y baseline */}
        <line
          x1={PAD_LEFT}
          x2={W - PAD_RIGHT}
          y1={H - PAD_BOTTOM}
          y2={H - PAD_BOTTOM}
          className="stroke-vt-border"
          strokeWidth={1}
        />
        {buckets.map((b, i) => {
          const v = totals[b] ?? 0;
          const h = max > 0 ? (v / max) * innerH : 0;
          const x = PAD_LEFT + i * colW + (colW - barW) / 2;
          const y = H - PAD_BOTTOM - h;
          const isActive = activeBucket === b;
          return (
            <g
              key={b}
              className="cursor-pointer"
              onClick={() => onBucketClick(b)}
              role="button"
              aria-label={`${BUCKET_LABEL[b]}: ${compactInr(v)}`}
            >
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                rx={4}
                className={`${BUCKET_BAR_COLOR[b]} ${isActive ? 'opacity-100' : 'opacity-80 hover:opacity-100'}`}
              />
              <text
                x={x + barW / 2}
                y={Math.max(PAD_TOP + 10, y - 6)}
                textAnchor="middle"
                className="fill-vt-text-muted text-[10px] font-medium"
              >
                {compactInr(v)}
              </text>
              <text
                x={PAD_LEFT + i * colW + colW / 2}
                y={H - PAD_BOTTOM + 16}
                textAnchor="middle"
                className="fill-vt-text-muted text-[10px]"
              >
                {BUCKET_LABEL[b]}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
